// Curated "my foods" presets loaded from src/data/myFoods.json. The JSON
// is the user's own diet plan, exported from their routine assistant. Edit
// the JSON to update — no DB, no migration. See docs in the JSON header
// fields (currentPhase, dailyTargets) for context.

import data from '../data/myFoods.json'

export const MY_FOODS = Array.isArray(data?.foods) ? data.foods : []
export const SUGGESTED_TARGETS = data?.dailyTargets || null
export const CURRENT_PHASE = data?.currentPhase || null

// Pick the localized name for the active UI language, falling back to
// English if the Arabic name is missing (some entries like supplements
// only have a single name).
export function pickName(food, lang) {
  if (lang === 'ar' && food.name_ar) return food.name_ar
  return food.name_en
}

// Foods carrying a `unit_en` and `grams_per_unit > 1` are logged in their
// natural unit (eggs, slices, scoops, cups, cans, tbsp). Anything else
// (raw meat, fish, rice, salad veg) is logged in grams — that's how the
// user actually weighs them.
export function hasNaturalUnit(food) {
  return !!food?.unit_en && Number(food.grams_per_unit) > 1
}

// Short label next to the count input. Arabic stays singular at all
// counts (informal but readable: "3 بيضة"). English appends "s" for
// counts != 1, with abbreviations (tsp, tbsp) and "g" left alone.
const EN_ABBREVIATIONS = new Set(['tsp', 'tbsp', 'oz', 'g'])

export function unitLabel(food, count, lang) {
  if (!hasNaturalUnit(food)) return lang === 'ar' ? 'جم' : 'g'
  const singular = lang === 'ar' ? (food.unit_ar || food.unit_en) : food.unit_en
  if (lang === 'ar') return singular
  if (count === 1 || EN_ABBREVIATIONS.has(singular)) return singular
  if (singular.endsWith('s') || singular.endsWith('x')) return singular
  return `${singular}s`
}

// Filter the preset list to a single meal slot. The JSON allows a food
// to belong to multiple slots (e.g. milk appears in breakfast + snack
// + dinner), so we match by inclusion.
export function getMyFoodsForMeal(mealType) {
  return MY_FOODS.filter((f) => Array.isArray(f.meal_types) && f.meal_types.includes(mealType))
}

// Group ALL foods by relevance to the active meal slot — foods tagged
// for this slot land in `suggested`, everything else in `others`. The
// builder shows both so the user can still pick toast for lunch even
// though it's flagged as a breakfast/snack food. The original
// meal_types tagging is just a recommendation, not a restriction.
export function getMyFoodsGroupedForMeal(mealType) {
  const suggested = []
  const others = []
  for (const f of MY_FOODS) {
    if (Array.isArray(f.meal_types) && f.meal_types.includes(mealType)) {
      suggested.push(f)
    } else {
      others.push(f)
    }
  }
  return { suggested, others }
}

// Convert a preset into the values the meal form expects, scaled from
// per-100g to the typical portion size. Macros are rounded to one
// decimal so the UI doesn't show noisy fractions.
export function buildEntryFromPreset(food, lang) {
  const portion = Number(food.typical_portion_g) || 100
  const scale = portion / 100
  const round1 = (n) => Math.round((Number(n) || 0) * scale * 10) / 10
  const roundInt = (n) => Math.round((Number(n) || 0) * scale)
  return {
    name: pickName(food, lang),
    kcal: roundInt(food.kcal_per_100g),
    protein_g: round1(food.protein_per_100g),
    carbs_g: round1(food.carbs_per_100g),
    fat_g: round1(food.fat_per_100g),
    portion_g: portion,
  }
}
